/**
 * Inquiry Sequence — "The Order of Questions — and Why I Ask Each One"
 *
 * A coach's flow for disciplined inquiry that surfaces the controlling question
 * before moving to answers. The page at /inquiry-sequence renders from these structures.
 */

export interface InquiryStep {
  number: number;
  name: string;
  subtitle?: string;
  /** Primary question when there is one canonical phrasing. */
  question?: string;
  /** Valid questions when multiple options exist for this step. */
  questions?: string[];
  purpose: string[];
  moveOnWhen?: string[];
  notes?: string[];
  /** Step has no spoken question (silence or declarative only). */
  noQuestion?: boolean;
  /** Declarative capability statement — only if invited. */
  capabilityStatement?: string;
  /** Internal reminder for the coach (not spoken). */
  internalReminder?: string;
}

export interface InquiryTableRow {
  step: string;
  questions: string[];
  /** Display when there is no question (silence or declarative). */
  note?: string;
}

export interface InquiryLapRow {
  lap: string;
  step: string;
  questions: string[];
  note?: string;
}

export interface LanguageAlternateGroup {
  title: string;
  alternates: string[];
  useWhen: string[];
}

export const HOW_TO_USE = [
  'This is a flow, not a script',
  'You may take more than one lap',
  'You do not have to ask every question',
  'You move forward only when the purpose has been satisfied',
];

export const IMPLICIT_FRAME = {
  name: 'Implicit Frame (Not Spoken)',
  posture: 'Pay-it-forward, service-oriented, non-extractive',
  purpose: [
    'Create safety',
    'Remove performance pressure',
    'Signal curiosity over agenda',
  ],
  reminder: 'No words. This is stance.',
};

export const INQUIRY_STEPS: InquiryStep[] = [
  {
    number: 1,
    name: 'Precipitating Event',
    subtitle: 'Anchor in reality',
    question:
      'What actually prompted the curiosity now? Was there something specific that happened?',
    purpose: [
      'Ground the conversation in real events',
      'Establish relevance and urgency',
      'Avoid abstract or speculative discussion',
    ],
    moveOnWhen: ['Concrete examples or situations are named'],
  },
  {
    number: 2,
    name: 'Desired Change',
    subtitle: 'Name the delta',
    question:
      'When you stop for a moment and think about it, what do you wish were working differently?',
    purpose: [
      'Surface aspiration without solutioning',
      'Clarify why this matters',
      'Reveal emotional and practical stakes',
    ],
    moveOnWhen: ['They describe a meaningful difference, not just complaints'],
  },
  {
    number: 3,
    name: 'Language Clarification',
    subtitle: 'Slow down loaded words',
    question: 'You mentioned ___ — what does that mean in practice?',
    notes: [
      'Common words: alignment, priority, progress, ownership, success, risk, culture',
    ],
    purpose: [
      'Prevent false agreement',
      'Translate abstraction into observable behavior',
      'Reveal mismatched mental models',
    ],
    moveOnWhen: ['The word is defined behaviorally, not rhetorically'],
  },
  {
    number: 4,
    name: 'First Relevance Ranking',
    subtitle: 'Test for a controlling question',
    question: 'If you could only clarify one thing here, what should it be?',
    purpose: [
      'Force prioritization',
      'Test whether the space is ready to collapse',
      'Surface a candidate controlling question',
    ],
    notes: ['If the answer is still broad or list-like: do not force it. Take another lap.'],
  },
  {
    number: 5,
    name: 'Blocker / Tension Discovery',
    subtitle: 'Why clarity hasn\'t stabilized yet',
    questions: [
      'What makes that hard to get clear on?',
      'Where do people disagree most?',
      'What keeps getting pushed to the side?',
    ],
    notes: ['Use the third option on a late lap.'],
    purpose: [
      'Surface conflict, avoidance, or fear',
      'Reveal why progress keeps stalling',
      'Add emotional weight and realism',
    ],
    moveOnWhen: ['Disagreement or tension is named explicitly'],
  },
  {
    number: 6,
    name: 'Second Relevance Ranking',
    subtitle: 'Now it usually lands',
    question: 'Given all that, what question has to be answered first?',
    purpose: [
      'Identify the true controlling question',
      'Collapse complexity using prior context',
      'Let clarity emerge from their reasoning',
    ],
    moveOnWhen: [
      'One question organizes the rest',
      'Speech slows and becomes more confident',
    ],
  },
  {
    number: 7,
    name: 'Controlling Question Test',
    subtitle: 'Validate it really matters',
    question: 'If that were clear, what would become easier?',
    purpose: [
      'Confirm downstream impact',
      'Ensure this question is worth stopping for',
    ],
    moveOnWhen: ['Clear second-order effects are named'],
  },
  {
    number: 8,
    name: 'Hold',
    subtitle: 'Do nothing',
    noQuestion: true,
    purpose: [
      'Preserve ownership of clarity',
      'Avoid collapsing inquiry into explanation',
    ],
    notes: ['Silence', 'No interpretation', 'No framework naming'],
    internalReminder: 'The value already happened.',
  },
  {
    number: 9,
    name: 'Capability Statement',
    subtitle: 'Only if invited',
    noQuestion: true,
    capabilityStatement:
      'I help teams slow down just enough to agree on the question that actually deserves their best thinking—before they move on to answers.',
    purpose: [
      'Name capability without pitching',
      'Connect directly to their clarity',
      'Invite curiosity, not commitment',
    ],
    notes: ['Stop there.'],
  },
];

export const END_STATE_CHECK = [
  'A controlling question is explicit',
  'It organizes everything else',
  'No one feels rushed to solve it',
];

export const FOOTER_LINE =
  'Clarity emerges through disciplined inquiry, not clever questions.';

export const SINGLE_PASS_TABLE: InquiryTableRow[] = [
  {
    step: '1. Precipitating Event',
    questions: [
      'What actually prompted the curiosity now? Was there something specific that happened?',
    ],
  },
  {
    step: '2. Desired Change',
    questions: [
      'When you stop for a moment and think about it, what do you wish were working differently?',
    ],
  },
  {
    step: '3. Language Clarification (Loaded Words)',
    questions: ['You mentioned ___ — what does that mean in practice?'],
  },
  {
    step: '4. First Relevance Ranking',
    questions: ['If you could only clarify one thing here, what should it be?'],
  },
  {
    step: '5. Blocker / Tension Discovery',
    questions: [
      'What makes that hard to get clear on?',
      'Where do people disagree most?',
      'What keeps getting pushed to the side?',
    ],
  },
  {
    step: '6. Second Relevance Ranking (Controlling Question)',
    questions: ['Given all that, what question has to be answered first?'],
  },
  {
    step: '7. Controlling Question Test',
    questions: ['If that were clear, what would become easier?'],
  },
  {
    step: '8. Hold (No Question)',
    questions: [],
    note: 'Silence — no question asked',
  },
  {
    step: '9. Capability Introduction (Only if Invited)',
    questions: [],
    note: 'No question — declarative sentence if prompted',
  },
];

export const THREE_LAP_TABLE: InquiryLapRow[] = [
  {
    lap: 'Lap 1',
    step: 'Precipitating Event',
    questions: [
      'What actually prompted the curiosity now? Was there something specific that happened?',
    ],
  },
  {
    lap: 'Lap 1',
    step: 'Desired Change',
    questions: [
      'When you stop for a moment and think about it, what do you wish were working differently?',
    ],
  },
  {
    lap: 'Lap 1',
    step: 'Language Clarification',
    questions: ['You mentioned ___ — what does that mean in practice?'],
  },
  {
    lap: 'Lap 1',
    step: 'First Relevance Ranking (Attempt)',
    questions: ['If you could only clarify one thing here, what should it be?'],
  },
  {
    lap: 'Lap 2',
    step: 'Language Clarification (Repeat as Needed)',
    questions: ['You mentioned ___ — what does that mean in practice?'],
  },
  {
    lap: 'Lap 2',
    step: 'Blocker / Tension Discovery',
    questions: [
      'What makes that hard to get clear on?',
      'Where do people disagree most?',
      'What keeps getting pushed to the side?',
    ],
  },
  {
    lap: 'Lap 2',
    step: 'Second Relevance Ranking (Attempt)',
    questions: ['If you could only clarify one thing here, what should it be?'],
  },
  {
    lap: 'Lap 3',
    step: 'Blocker / Tension (Final Pass)',
    questions: ['What makes this hard to decide?', 'What\'s still unresolved here?'],
  },
  {
    lap: 'Lap 3',
    step: 'Controlling Question Identification',
    questions: ['Given all that, what question has to be answered first?'],
  },
  {
    lap: 'Lap 3',
    step: 'Controlling Question Test',
    questions: ['If that were clear, what would become easier?'],
  },
  {
    lap: 'After Lap 3',
    step: 'Hold',
    questions: [],
    note: 'No question — silence',
  },
  {
    lap: 'After Lap 3',
    step: 'Capability Introduction (Only if Invited)',
    questions: [],
    note: 'No question — declarative statement if prompted',
  },
];

export const THREE_LAP_REMINDER =
  'Multiple laps are normal. Forcing convergence early produces the wrong question.';

export const USAGE_REMINDER = [
  'You may take multiple laps',
  'You ask one question at a time',
  'You move forward only when the step\'s purpose is satisfied',
  'Silence is a valid and necessary step',
];

export const LANGUAGE_CLARIFICATION_RULE =
  'If the word can mean different things to different people, intercept it. You\'re not asking for a definition. You\'re asking for a shared picture.';

export const LANGUAGE_ALTERNATES: LanguageAlternateGroup[] = [
  {
    title: 'Core Neutral Alternates',
    alternates: [
      'How does that show up day to day?',
      'What does that look like on the ground?',
      'How would I recognize that if I saw it?',
      'What\'s an example of that?',
    ],
    useWhen: [
      'You want clarity without friction',
      'The relationship is early or formal',
    ],
  },
  {
    title: 'Behavior-Focused Alternates',
    alternates: [
      'What would people be doing differently if that were true?',
      'What changes in behavior when that\'s present?',
      'What does someone actually do in that case?',
    ],
    useWhen: [
      'Action matters more than definition',
      'You suspect verbal agreement is masking differences',
    ],
  },
  {
    title: 'Decision-Oriented Alternates',
    alternates: [
      'How does that affect the decisions people make?',
      'What does that change about what gets chosen or deprioritized?',
      'Where would that show up in a real decision?',
    ],
    useWhen: [
      'The issue is prioritization or trade-offs',
      'You\'re close to a controlling question',
    ],
  },
  {
    title: 'Evidence / Proof Alternates',
    alternates: [
      'What evidence would tell you that\'s actually happening?',
      'How would you know this isn\'t just talk?',
      'What would convince a skeptic?',
    ],
    useWhen: [
      'You hear confidence without grounding',
      'You want to surface standards without confrontation',
    ],
  },
  {
    title: 'Time-Bound Alternates',
    alternates: [
      'What would be different a month from now if that were true?',
      'What would we notice first?',
      'What would change in the next quarter?',
    ],
    useWhen: [
      'The future is vague',
      'Momentum or progress is in question',
    ],
  },
  {
    title: 'Softened / Relational Alternates',
    alternates: [
      'Help me understand what you mean by that.',
      'Say a bit more about that.',
      'How are you thinking about that?',
    ],
    useWhen: [
      'You sense sensitivity or status risk',
      'You want them to lead the explanation',
    ],
  },
];

/** Ordered list of all valid questions across the sequence (for quick reference). */
export const ALL_QUESTIONS = [
  'What actually prompted the curiosity now? Was there something specific that happened?',
  'When you stop for a moment and think about it, what do you wish were working differently?',
  'You mentioned ___ — what does that mean in practice?',
  'If you could only clarify one thing here, what should it be?',
  'What makes that hard to get clear on?',
  'Where do people disagree most?',
  'What keeps getting pushed to the side?',
  'Given all that, what question has to be answered first?',
  'If that were clear, what would become easier?',
];
